import React, { useState, useRef, useEffect } from 'react';
import { GradientPicker } from 'react-linear-gradient-picker';
import { HexAlphaColorPicker } from 'react-colorful';
import 'react-linear-gradient-picker/dist/index.css';

interface BackgroundLayer {
  id: string;
  type: 'linear-gradient' | 'radial-gradient' | 'image';
  value: string; // CSS gradient string or image URL
}

interface PropertyBackgroundLayersProps {
  layers: BackgroundLayer[];
  onChange: (layers: BackgroundLayer[]) => void;
}

// Wrapper component for react-colorful to work with react-linear-gradient-picker
const WrappedColorPicker = ({ onSelect, ...rest }: any) => {
  return (
    <HexAlphaColorPicker
      {...rest}
      onChange={(color: string) => {
        onSelect(color);
      }}
    />
  );
};

export const PropertyBackgroundLayers: React.FC<PropertyBackgroundLayersProps> = ({
  layers,
  onChange
}) => {
  const [expandedLayerId, setExpandedLayerId] = useState<string | null>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Update picker position when expanded
  useEffect(() => {
    if (expandedLayerId) {
      const button = buttonRefs.current.get(expandedLayerId);
      if (button) {
        const rect = button.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + 4,
          left: rect.left
        });
      }
    }
  }, [expandedLayerId]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!expandedLayerId) return;

    const handleClickOutside = (e: MouseEvent) => {
      const button = buttonRefs.current.get(expandedLayerId);
      if (
        pickerRef.current && 
        !pickerRef.current.contains(e.target as Node) &&
        button &&
        !button.contains(e.target as Node)
      ) {
        setExpandedLayerId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedLayerId]);

  const addLayer = () => {
    const newLayer: BackgroundLayer = {
      id: `layer-${Date.now()}`,
      type: 'linear-gradient',
      value: 'linear-gradient(90deg, rgba(224, 56, 201, 1.00) 0%, rgba(246, 203, 45, 1.00) 100%)'
    };
    onChange([...layers, newLayer]);
  };

  const removeLayer = (id: string) => {
    onChange(layers.filter(layer => layer.id !== id));
    if (expandedLayerId === id) {
      setExpandedLayerId(null);
    }
  };

  const updateLayer = (id: string, updates: Partial<BackgroundLayer>) => {
    const updatedLayers = layers.map(layer =>
      layer.id === id ? { ...layer, ...updates } : layer
    );
    onChange(updatedLayers);
  };

  const changeLayerType = (id: string, newType: BackgroundLayer['type']) => {
    const layer = layers.find(l => l.id === id);
    if (!layer || layer.type === newType) return;

    let newValue = '';
    if (newType === 'linear-gradient') {
      newValue = 'linear-gradient(90deg, rgba(224, 56, 201, 1.00) 0%, rgba(246, 203, 45, 1.00) 100%)';
    } else if (newType === 'radial-gradient') {
      newValue = 'radial-gradient(circle, rgba(224, 56, 201, 1.00) 0%, rgba(246, 203, 45, 1.00) 100%)';
    } else {
      newValue = '';
    }

    updateLayer(id, { type: newType, value: newValue });
  };

  const parseGradient = (gradientString: string, type: BackgroundLayer['type']) => {
    // Parse gradient string into palette format for react-linear-gradient-picker
    let angle = '90';
    let shape = 'circle';
    
    if (type === 'linear-gradient') {
      const angleMatch = gradientString.match(/(\d+)deg/);
      angle = angleMatch ? angleMatch[1] : '90';
    } else if (type === 'radial-gradient') {
      const shapeMatch = gradientString.match(/radial-gradient\((circle|ellipse)/);
      shape = shapeMatch ? shapeMatch[1] : 'circle';
    }
    
    const colorStops = [];
    const regex = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)\s*([\d.]+)%/g;
    let match;
    
    while ((match = regex.exec(gradientString)) !== null) {
      const [, r, g, b, a = '1', offset] = match;
      const alpha = Math.round(parseFloat(a) * 255).toString(16).padStart(2, '0');
      const hex = `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}${alpha}`;
      
      // Convert CSS percentage to picker format:
      // - CSS has "11.2%" which is 11.2
      // - Picker expects: 0.112 for 11.2%, and 100 for 100%
      let offsetNumber = parseFloat(offset);
      
      // Clamp to prevent stops at exactly 100% (which get clipped in the picker UI)
      // 99.9% is effectively the same visually but stays interactive
      if (offsetNumber >= 100) {
        offsetNumber = 99.9;
      } else if (offsetNumber < 100) {
        // Convert percentage to decimal (11.2% -> 0.112)
        offsetNumber = offsetNumber / 100;
      }
      
      colorStops.push({ 
        id: `stop-${colorStops.length}`, // Required by react-linear-gradient-picker
        offset: offsetNumber,
        color: hex 
      });
    }
    
    // Sort by offset to ensure correct order
    colorStops.sort((a, b) => a.offset - b.offset);
    
    // Re-assign IDs after sorting to maintain sequential order
    const sortedStops = colorStops.map((stop, index) => ({
      ...stop,
      id: `stop-${index}`
    }));
    
    return { angle, shape, palette: sortedStops };
  };

  const buildGradientString = (palette: any[], angleOrShape: string, type: BackgroundLayer['type']) => {
    // Convert palette format back to CSS gradient string
    // Sort palette by offset before building
    const sortedPalette = [...palette].sort((a, b) => {
      const offsetA = typeof a.offset === 'number' ? a.offset : parseFloat(a.offset);
      const offsetB = typeof b.offset === 'number' ? b.offset : parseFloat(b.offset);
      return offsetA - offsetB;
    });
    
    const stops = sortedPalette
      .map(stop => {
        const color = stop.color;
        // Convert hex to rgba
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        const a = color.length === 9 ? (parseInt(color.slice(7, 9), 16) / 255).toFixed(2) : '1.00';
        
        // The picker returns offset in mixed format:
        // - If < 1: it's a decimal (0.7 = 70%)
        // - If >= 1: it's already in percentage (100 = 100%)
        let offsetValue = typeof stop.offset === 'number' ? stop.offset : parseFloat(stop.offset);
        if (offsetValue < 1) {
          offsetValue = offsetValue * 100;
        }
        const offset = offsetValue.toFixed(2);
        
        return `rgba(${r}, ${g}, ${b}, ${a}) ${offset}%`;
      })
      .join(', ');
    
    if (type === 'linear-gradient') {
      return `linear-gradient(${angleOrShape}deg, ${stops})`;
    } else {
      return `radial-gradient(${angleOrShape}, ${stops})`;
    }
  };

  const getGradientPreview = (gradientString: string) => {
    return gradientString;
  };

  const expandedLayer = layers.find(l => l.id === expandedLayerId);
  const expandedGradientData = expandedLayer && (expandedLayer.type === 'linear-gradient' || expandedLayer.type === 'radial-gradient')
    ? parseGradient(expandedLayer.value, expandedLayer.type)
    : null;

  // Icon components for layer types
  const LayerTypeIcon = ({ type, size = 32 }: { type: BackgroundLayer['type']; size?: number }) => {
    if (type === 'linear-gradient') {
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="rounded">
          <defs>
            <linearGradient id={`linear-icon-${size}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#000', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#fff', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <rect width="32" height="32" fill={`url(#linear-icon-${size})`} rx="3" />
        </svg>
      );
    } else if (type === 'radial-gradient') {
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className="rounded">
          <defs>
            <radialGradient id={`radial-icon-${size}`}>
              <stop offset="0%" style={{ stopColor: '#fff', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#000', stopOpacity: 1 }} />
            </radialGradient>
          </defs>
          <rect width="32" height="32" fill={`url(#radial-icon-${size})`} rx="3" />
        </svg>
      );
    } else {
      return (
        <div 
          className="bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center text-gray-700 dark:text-gray-300 font-medium border border-gray-300 dark:border-gray-600"
          style={{ width: size, height: size, fontSize: size * 0.3 }}
        >
          IMG
        </div>
      );
    }
  };

  // Dropdown for layer type selection
  const LayerTypeSelector = ({ layer }: { layer: BackgroundLayer }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (e: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const types: { value: BackgroundLayer['type']; label: string }[] = [
      { value: 'linear-gradient', label: 'Linear Gradient' },
      { value: 'radial-gradient', label: 'Radial Gradient' },
      { value: 'image', label: 'Image' }
    ];

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors border border-transparent hover:border-gray-300 dark:hover:border-gray-600"
          title="Change layer type"
        >
          <LayerTypeIcon type={layer.type} size={28} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 z-[10000] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl overflow-hidden min-w-[200px]">
            {types.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  changeLayerType(layer.id, value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                  layer.type === value
                    ? 'bg-cyan-600 text-white'
                    : 'text-gray-900 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex-shrink-0">
                  <LayerTypeIcon type={value} size={24} />
                </div>
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="space-y-2">
        {/* Header with Add button */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-gray-900 dark:text-gray-300 text-sm font-medium">Fill Layers</span>
          <button
            type="button"
            onClick={addLayer}
            className="w-7 h-7 flex items-center justify-center bg-cyan-600 hover:bg-cyan-500 rounded text-white text-base font-medium transition-all shadow-sm hover:shadow"
            title="Add layer"
          >
            +
          </button>
        </div>

        {/* Layer list */}
        <div className="space-y-2">
          {layers.map((layer) => (
            <div
              key={layer.id}
              className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-lg p-2 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
            >
              {/* Type selector */}
              <LayerTypeSelector layer={layer} />

              {/* Layer preview/button */}
              <button
                ref={(el) => {
                  if (el) buttonRefs.current.set(layer.id, el);
                  else buttonRefs.current.delete(layer.id);
                }}
                type="button"
                onClick={() => setExpandedLayerId(expandedLayerId === layer.id ? null : layer.id)}
                className="flex-1 min-w-0 hover:opacity-80 transition-opacity"
              >
                {/* Layer content */}
                {layer.type === 'linear-gradient' || layer.type === 'radial-gradient' ? (
                  <div
                    className="h-8 rounded-md border border-gray-700 shadow-inner"
                    style={{ background: getGradientPreview(layer.value) }}
                  />
                ) : (
                  <div className="h-8 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <span className="text-gray-600 dark:text-gray-400 text-xs truncate px-2">
                      {layer.value || 'No image selected'}
                    </span>
                  </div>
                )}
              </button>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => removeLayer(layer.id)}
                className="w-8 h-8 flex items-center justify-center hover:bg-red-600 rounded text-gray-600 dark:text-gray-400 hover:text-white transition-all text-xl leading-none"
                title="Remove layer"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {layers.length === 0 && (
          <div className="text-gray-500 dark:text-gray-500 text-xs text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <p className="mb-1 text-gray-600 dark:text-gray-500">No fill layers</p>
            <p className="text-gray-500 dark:text-gray-600">Click + to add one</p>
          </div>
        )}
      </div>

      {/* Gradient picker popup */}
      {expandedLayerId && expandedLayer && (expandedLayer.type === 'linear-gradient' || expandedLayer.type === 'radial-gradient') && expandedGradientData && (
        <div
          ref={pickerRef}
          className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl p-4"
          style={{
            top: `${pickerPosition.top}px`,
            left: `${pickerPosition.left}px`,
            minWidth: '300px'
          }}
        >
          <div className="space-y-3">
            <div
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Right-click detected on picker', e.target);
                
                // Don't allow deleting if only 2 stops remain (gradient needs at least 2)
                if (expandedGradientData.palette.length <= 2) {
                  console.log('Not enough stops to delete (need at least 2)');
                  return;
                }
                
                // Find the main picker container (the csh div with width: 245px)
                const pickerDiv = (e.currentTarget as HTMLElement).querySelector('[class*="csh"]') || 
                                  (e.currentTarget as HTMLElement);
                console.log('Picker div found:', pickerDiv);
                if (!pickerDiv) {
                  console.log('Could not find picker div');
                  return;
                }
                
                // Get the bounds of the picker
                const rect = pickerDiv.getBoundingClientRect();
                const relativeX = e.clientX - rect.left;
                const pickerWidth = rect.width;
                console.log('Click position:', relativeX, 'out of', pickerWidth);
                
                // Calculate which stop is closest to the click position (as a percentage)
                const clickPercentage = (relativeX / pickerWidth) * 100;
                console.log('Click percentage:', clickPercentage);
                
                // Find the closest stop
                let closestIndex = 0;
                let closestDistance = Math.abs(expandedGradientData.palette[0].offset - clickPercentage);
                console.log('Current palette:', expandedGradientData.palette);
                
                for (let i = 1; i < expandedGradientData.palette.length; i++) {
                  const offset = expandedGradientData.palette[i].offset < 1 
                    ? expandedGradientData.palette[i].offset * 100 
                    : expandedGradientData.palette[i].offset;
                  const distance = Math.abs(offset - clickPercentage);
                  
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = i;
                  }
                }
                
                console.log('Closest stop index:', closestIndex, 'distance:', closestDistance);
                
                // Only delete if click was reasonably close to the stop (within 15% of picker width)
                if (closestDistance < 15) {
                  console.log('Deleting stop at index', closestIndex);
                  const newPalette = expandedGradientData.palette
                    .filter((_, idx) => idx !== closestIndex)
                    .map((stop, index) => ({
                      ...stop,
                      id: `stop-${index}` // Re-assign sequential IDs after deletion
                    }));
                  const angleOrShape = expandedLayer.type === 'linear-gradient' 
                    ? expandedGradientData.angle 
                    : expandedGradientData.shape;
                  const newGradient = buildGradientString(newPalette, angleOrShape, expandedLayer.type);
                  updateLayer(expandedLayerId, { value: newGradient });
                } else {
                  console.log('Click too far from any stop');
                }
              }}
            >
            <GradientPicker
              {...{
                width: 250,
                paletteHeight: 32,
                palette: expandedGradientData.palette,
                onPaletteChange: (palette: any[]) => {
                  // Ensure all palette items have IDs (library may add stops without IDs)
                  const paletteWithIds = palette.map((stop, index) => ({
                    ...stop,
                    id: stop.id || `stop-${index}`
                  }));
                  
                  const angleOrShape = expandedLayer.type === 'linear-gradient' 
                    ? expandedGradientData.angle 
                    : expandedGradientData.shape;
                  const newGradient = buildGradientString(paletteWithIds, angleOrShape, expandedLayer.type);
                  updateLayer(expandedLayerId, { value: newGradient });
                }
              }}
            >
              <WrappedColorPicker />
            </GradientPicker>
            </div>

            {/* Angle control for linear gradient */}
            {expandedLayer.type === 'linear-gradient' && (
              <div className="flex items-center gap-2 pt-3 border-t border-gray-700 mt-3">
                <label className="text-gray-900 dark:text-gray-300 text-sm font-medium w-16">Angle</label>
                <input
                  type="number"
                  value={expandedGradientData.angle}
                  onChange={(e) => {
                    const newGradient = buildGradientString(
                      expandedGradientData.palette,
                      e.target.value,
                      'linear-gradient'
                    );
                    updateLayer(expandedLayerId, { value: newGradient });
                  }}
                  className="flex-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:border-cyan-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  min="0"
                  max="360"
                />
                <span className="text-gray-600 dark:text-gray-400 text-sm w-4">°</span>
              </div>
            )}

            {/* Shape control for radial gradient */}
            {expandedLayer.type === 'radial-gradient' && (
              <div className="flex items-center gap-2 pt-3 border-t border-gray-700 mt-3">
                <label className="text-gray-900 dark:text-gray-300 text-sm font-medium w-16">Shape</label>
                <select
                  value={expandedGradientData.shape}
                  onChange={(e) => {
                    const newGradient = buildGradientString(
                      expandedGradientData.palette,
                      e.target.value,
                      'radial-gradient'
                    );
                    updateLayer(expandedLayerId, { value: newGradient });
                  }}
                  className="flex-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:border-cyan-500 focus:outline-none"
                >
                  <option value="circle">Circle</option>
                  <option value="ellipse">Ellipse</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

